import { FC, useState } from 'react';
import type { AdminFeatureFlag } from '@libs/types';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  IconButton,
  Switch,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { observer } from 'mobx-react-lite';

import { FeatureFlagKey } from '@libs/constants';
import { type SingularKeys, useTrans } from '@libs/i18n';
import { TrashIcon } from '@phosphor-icons/react';

import {
  useAdminFeatureFlags,
  useAdminTenants,
  useUpdateFeatureFlag,
  useUpdateTenantFeatureFlag,
} from '../api/admin-api';
import { adminButtonSx, adminColors } from '../components/adminTheme';

/** i18n key for each flag's human-facing label. Extend alongside FeatureFlagKey. */
const FLAG_LABEL_KEY: Record<FeatureFlagKey, SingularKeys> = {
  [FeatureFlagKey.REMEDIATION]: 'app.admin.features.flags.remediation',
  [FeatureFlagKey.ASSESSMENT_PROGRESS]: 'app.admin.features.flags.assessment_progress',
};

interface TenantOption {
  id: string;
  label: string;
}

/** Search-and-pick a tenant, choose on/off, and add it as an override. */
const AddOverrideRow: FC<{ flagKey: FeatureFlagKey; existingTenantIds: string[] }> = observer(
  ({ flagKey, existingTenantIds }) => {
    const theme = useTheme();
    const btn = adminButtonSx(theme);
    const { t } = useTrans();
    const [search, setSearch] = useState('');
    const [selectedTenant, setSelectedTenant] = useState<TenantOption | null>(null);
    const [forceOn, setForceOn] = useState(true);
    const { data: tenants } = useAdminTenants(search);
    const updateTenantFeatureFlag = useUpdateTenantFeatureFlag();

    const options: TenantOption[] = (tenants ?? [])
      .filter((tenant) => !existingTenantIds.includes(tenant.id))
      .map((tenant) => ({ id: tenant.id, label: tenant.displayName || tenant.name }));

    const handleAdd = () => {
      if (!selectedTenant) return;
      updateTenantFeatureFlag.mutate(
        { tenantId: selectedTenant.id, key: flagKey, enabled: forceOn },
        {
          onSuccess: () => {
            setSelectedTenant(null);
            setSearch('');
          },
        },
      );
    };

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
        <Autocomplete
          size="small"
          options={options}
          value={selectedTenant}
          onChange={(_, value) => setSelectedTenant(value)}
          onInputChange={(_, value) => setSearch(value)}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField {...params} placeholder={t('app.admin.features.addOverride.searchPlaceholder')} />
          )}
          sx={{ flex: 1, minWidth: 220 }}
        />
        <Button
          size="small"
          onClick={() => setForceOn((v) => !v)}
          sx={{ ...btn.neutral, fontSize: 12.5, px: 1.5, whiteSpace: 'nowrap' }}
        >
          {forceOn ? t('app.admin.features.forceOn') : t('app.admin.features.forceOff')}
        </Button>
        <Button
          size="small"
          disabled={!selectedTenant || updateTenantFeatureFlag.isPending}
          onClick={handleAdd}
          sx={{ ...btn.solid, fontSize: 12.5, px: 2, whiteSpace: 'nowrap' }}
        >
          {t('app.admin.features.addOverride.action')}
        </Button>
      </Box>
    );
  },
);

const FeatureFlagCard: FC<{ flag: AdminFeatureFlag }> = observer(({ flag }) => {
  const theme = useTheme();
  const c = adminColors(theme);
  const { t } = useTrans();
  const updateFeatureFlag = useUpdateFeatureFlag();
  const updateTenantFeatureFlag = useUpdateTenantFeatureFlag();

  return (
    <Box
      sx={{
        backgroundColor: c.surface,
        border: `1px solid ${c.border}`,
        borderRadius: 3,
        p: 2.5,
        mb: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Box>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: c.ink }}>
            {t(FLAG_LABEL_KEY[flag.key])}
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: c.sub, mt: 0.25 }}>
            {t('app.admin.features.globalDescription')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          <Typography
            sx={{ fontSize: 12.5, color: flag.globalEnabled ? c.green : c.sub, fontWeight: 600 }}
          >
            {flag.globalEnabled ? t('app.admin.features.on') : t('app.admin.features.off')}
          </Typography>
          <Switch
            checked={flag.globalEnabled}
            onChange={(_, checked) => updateFeatureFlag.mutate({ key: flag.key, enabled: checked })}
            disabled={updateFeatureFlag.isPending}
          />
        </Box>
      </Box>

      <Box sx={{ height: '1px', backgroundColor: c.border, my: 2 }} />

      <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: c.faint, letterSpacing: '0.06em', mb: 1 }}>
        {t('app.admin.features.overridesTitle')}
      </Typography>

      {flag.overrides.length === 0 ? (
        <Typography sx={{ fontSize: 12.5, color: c.sub }}>{t('app.admin.features.noOverrides')}</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {flag.overrides.map((override) => (
            <Box
              key={override.tenantId}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 0.75,
                px: 1.25,
                borderRadius: 2,
                backgroundColor: theme.palette.grey[100],
              }}
            >
              <Typography sx={{ fontSize: 13, color: c.ink }}>{override.tenantName}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  size="small"
                  label={
                    override.enabled ? t('app.admin.features.forcedOn') : t('app.admin.features.forcedOff')
                  }
                  sx={{
                    backgroundColor: override.enabled ? '#F0FBF5' : '#FFF1F0',
                    color: override.enabled ? '#157A47' : '#C0272E',
                    fontSize: 11.5,
                    height: 22,
                  }}
                />
                <IconButton
                  size="small"
                  title={t('app.admin.features.clearOverride')}
                  onClick={() =>
                    updateTenantFeatureFlag.mutate({
                      tenantId: override.tenantId,
                      key: flag.key,
                      enabled: null,
                    })
                  }
                  disabled={updateTenantFeatureFlag.isPending}
                >
                  <TrashIcon size={15} />
                </IconButton>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <AddOverrideRow flagKey={flag.key} existingTenantIds={flag.overrides.map((o) => o.tenantId)} />
    </Box>
  );
});

/**
 * Platform-admin control panel for runtime feature flags — the on/off switch
 * for shipping a feature dark and turning it on later without a redeploy, plus
 * per-organization overrides for piloting a feature with one tenant.
 */
export const AdminFeaturesPage: FC = observer(() => {
  const theme = useTheme();
  const c = adminColors(theme);
  const { t } = useTrans();
  const { data: flags, isLoading, isError } = useAdminFeatureFlags();

  return (
    <Box sx={{ flex: 1, overflow: 'auto', px: 3.5, py: 2.75 }}>
      <Box sx={{ mb: 2.25 }}>
        <Typography sx={{ fontSize: 23, fontWeight: 700, color: c.ink, mb: 0.375 }}>
          {t('app.admin.features.title')}
        </Typography>
        <Typography sx={{ fontSize: 13, color: c.sub }}>{t('app.admin.features.subtitle')}</Typography>
      </Box>

      {isLoading && <Typography sx={{ fontSize: 13, color: c.sub }}>{t('app.common.loading')}</Typography>}
      {isError && (
        <Typography sx={{ fontSize: 13, color: c.error }}>{t('app.admin.features.loadError')}</Typography>
      )}
      {flags?.map((flag) => (
        <FeatureFlagCard key={flag.key} flag={flag} />
      ))}
    </Box>
  );
});
